import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePathname, useRouter } from '@/routing/navigation'
import { AlertCircle } from '@digital-twins/design-system/icons'
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { WorkbookAssignmentCard } from '../../components/WorkbookAssignmentCard'
import { ImportActionBar } from '../../components/ImportActionBar'
import { DiscardImportSessionDialog } from '../../components/DiscardImportSessionDialog'
import { useAudienceImportSessionMutations } from '../../hooks/useAudienceImportSession'
import { useWorkbookProjection } from '../../hooks/useWorkbookProjection'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import {
  navigateToImportRoute,
  warmImportRouteCaches,
} from '../../lib/import-session-route-handoff'
import { useImportWizardContext } from '../../context/ImportWizardContext'

export function WorkbookAssignmentStep() {
  const { t } = useTranslation('app')
  const { sessionId, snapshot, mutateSnapshot, goToStep } = useImportWizardContext()
  const router = useRouter()
  const pathname = usePathname()
  const {
    assignSheets,
    isAssigningSheets,
    discard,
    isDiscarding,
    lastMutationRequestId,
  } = useAudienceImportSessionMutations(sessionId, {
    fallbackMachineVersion: snapshot?.machineVersion,
  })
  const {
    projection,
    error,
    isLoading,
  } = useWorkbookProjection(sessionId, {
    lastMutationRequestId,
  })
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [sheetSelection, setSheetSelection] = useState<{
    audienceSheet: string
    dictionarySheet: string | null
    canSubmit: boolean
  } | null>(null)

  const handleSheetChange = useCallback(
    (payload: { audienceSheet: string; dictionarySheet: string | null; canSubmit: boolean }) => {
      setSheetSelection(payload)
    },
    [],
  )

  async function handleSubmitSheets() {
    if (!sheetSelection?.canSubmit) return
    const ack = await assignSheets({
      audienceSheet: sheetSelection.audienceSheet,
      dictionarySheet: sheetSelection.dictionarySheet,
    })
    const ackRoute = ack?.flowSnapshot?.route
    if (ackRoute) {
      await warmImportRouteCaches({
        sessionId,
        targetRoute: ackRoute,
        ack,
      })
      navigateToImportRoute({
        sessionId,
        sourceRoute: pathname,
        targetRoute: ackRoute,
        router,
        trigger: 'assign_sheets',
      })
      return
    }
    const nextSnapshot = await mutateSnapshot()
    const route =
      nextSnapshot?.route ??
      snapshot?.links?.contextMaterials ??
      snapshot?.links?.requiredFields ??
      snapshot?.route
    navigateToImportRoute({
      sessionId,
      sourceRoute: pathname,
      targetRoute: route,
      router,
      trigger: 'assign_sheets_refresh',
    })
  }

  async function handleDiscard() {
    try {
      await discard()
      setDiscardDialogOpen(false)
      router.push('/audiences/new')
    } catch {}
  }

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-7 w-64 mx-auto" />
        <Skeleton className="h-5 w-80" />
        <Skeleton className="h-[300px] rounded-2xl" />
      </>
    )
  }

  if (error || !projection || !projection.sheetAssignment) {
    return (
      <>
        <div className="flex flex-col gap-2">
          <h2 className="font-display text-3xl leading-[1.2] tracking-tight text-foreground dark:text-white">
            Workbook assignment unavailable
          </h2>
          <p className="text-base leading-[1.6] text-muted-foreground">
            The workbook-selection step could not be loaded.
          </p>
        </div>
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error
              ? getApiErrorMessage(
                  error,
                  'The session may have advanced or expired before workbook assignment loaded.',
                )
              : 'The session may have advanced or expired before workbook assignment loaded.'}
          </AlertDescription>
        </Alert>
      </>
    )
  }

  return (
    <>
      <DiscardImportSessionDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={handleDiscard}
        isPending={isDiscarding}
      />

      <div className="flex flex-col gap-2">
        <h2 className="font-display text-3xl leading-[1.2] tracking-tight text-foreground dark:text-white">
          Which sheet has the data?
        </h2>
        <p className="text-base leading-[1.6] text-muted-foreground">
          Your file has multiple sheets — pick the one with respondent-level rows.
        </p>
      </div>

      <WorkbookAssignmentCard
        options={projection.sheetAssignment.options}
        audienceSheet={projection.sheetAssignment.audienceSheet}
        dictionarySheet={projection.sheetAssignment.dictionarySheet}
        isSaving={isAssigningSheets}
        onSubmit={handleSubmitSheets}
        onChange={handleSheetChange}
        externalCta
      />

      <ImportActionBar
        backLabel={t('audienceCreate.steps.upload')}
        onBack={() => goToStep('upload')}
        backDisabled={isAssigningSheets}
        nextLabel={t('audienceCreate.continue')}
        onNext={() => void handleSubmitSheets()}
        nextDisabled={!sheetSelection?.canSubmit || isAssigningSheets}
        nextLoading={isAssigningSheets}
        nextLoadingLabel={t('audienceCreate.saving')}
      />
    </>
  )
}
